export const AI_LEADERS_PROGRAM = {
  name: "AI 30",
  headline: "Turn your team into confident AI leaders.",
  commitment: "In 30 days, help your people choose valuable work, build or improve AI workflows, measure the results, and lead adoption together.",
  durationDays: 30,
  seatPrice: 2500,
  pricingNote: "Volume discounts quoted by cohort size.",
  liveHours: 8,
  supportDays: 30,
  scopeCallUrl: "https://calendar.app.google/K3DCsnXnXpYR1CKp9",
  contactEmail: "todd@technical-leaders.com",
};

const proposalSubject = "Team proposal request: AI 30";
export const buildAI30ProposalHref = (seats?: number) => {
  const validSeats = Number.isSafeInteger(seats) && (seats ?? 0) > 0 ? seats : undefined;
  const proposalBody = [
    "Hi Tech Leaders team,",
    "",
    "I'd like a proposal for AI 30, the 30-day program for our team. The base price is $2,500 per seat, with volume discounts quoted by cohort size.",
    "We want to build useful AI workflows, measure the results, and help our leaders bring their teams with them.",
    "",
    "Company or member organization: ",
    `Number of seats: ${validSeats ?? ""}`,
    "Preferred format (on-site or virtual): ",
    "Preferred timing: ",
    "Recurring work we want to improve: ",
    "Approved tools or data requirements: ",
    "Priorities or questions: ",
    "",
    "Thanks!",
  ].join("\n");
  return `mailto:${AI_LEADERS_PROGRAM.contactEmail}?subject=${encodeURIComponent(proposalSubject)}&body=${encodeURIComponent(proposalBody)}`;
};
export const AI_LEADERS_PROPOSAL_HREF = buildAI30ProposalHref();

// Sales-page synthesis of the delivered practical and leadership strands.
// Sources: elevateCoreVoice.ts outcomes; elevateAgentSetup.ts Session 4;
// elevateLeadership.ts sessionDefinitions, value-outcomes, and thin-governance.
// These are capabilities, not a new session count or commercial schedule.
export const AI_LEADERS_CURRICULUM = [
  {
    title: "Give AI the context it needs",
    practice: "Write a Chief of Staff brief you can use again. Include your role, priorities, approved sources, instructions, and what a good result looks like.",
    leadership: "Know where AI can help, where it can fail, and which decisions still need your judgment.",
    takeaway: "Instructions you can use again",
  },
  {
    title: "Choose work worth improving",
    practice: "Look at the work that repeats in your calendar. Choose a task, then define what starts it, what goes in, what comes out, who checks it, and what done means.",
    leadership: "Check the value and risk before you start. Make sure your team is ready and someone owns the work.",
    takeaway: "A workflow with a clear scope",
  },
  {
    title: "Choose an approved tool that fits the job",
    practice: "Compare tools against your task. Check which sources they can use, then plan a fair comparison with how you do the work today.",
    leadership: "Find out what's getting in the way of adoption and what people need to try a different way of working.",
    takeaway: "A tool choice you can explain",
  },
  {
    title: "Set up, run, and review an agent",
    practice: "Save the setup and open it again. Review a draft, test what happens when information is missing or conflicts, and try a sample schedule where your workspace supports it.",
    leadership: "Make a clear case for your sponsor and name the person who checks the work. Keep consequential actions with the person responsible.",
    takeaway: "A reviewed run and a tested setup or worksheet",
  },
  {
    title: "Use the results to improve the next run",
    practice: "Compare quality and total effort. Change one instruction and test again. Record whether to keep going, revise the workflow, or stop.",
    leadership: "Decide who owns the workflow, when to review it, and how people can give feedback and make it better.",
    takeaway: "A measured comparison and a clear next step",
  },
];

export const AI_LEADERS_TAKEAWAYS = [
  { title: "Instructions your team can reuse", description: "Keep the role context, approved sources, examples, and review checks together so your team can repeat the work and share the method." },
  { title: "A record of what you tried", description: "Keep your workflow brief, tool choice, reviewed drafts, and feedback. If access is blocked, use a worksheet to show what still needs to be set up." },
  { title: "A clear next step", description: "Compare the results with your current process. Recommend what to continue, revise, or stop, who owns it, and what support they need." },
];

export const AI_LEADERS_ADOPTION_PLAN = [
  {
    title: "Name your champion",
    description: "Choose someone who helps people try the workflow, answers questions, and keeps the sponsor informed.",
  },
  {
    title: "Listen and act on feedback",
    description: "Ask people what helps, what gets in the way, and what support they need. Use their feedback to improve how the team works.",
  },
  {
    title: "Check adoption",
    description: "Agree on who will use the workflow and how you will check use, quality, and results before expanding it.",
  },
];

export const AI_LEADERS_INCLUSIONS = [
  {
    title: "8 hours of live training",
    description: "Two-hour sessions where you build and review workflows while practicing the leadership decisions behind them. Recordings are available.",
  },
  {
    title: "AI SOS playbook + adoption plan",
    description: "Choose useful work, check the results, and plan adoption with a champion, team feedback, and clear measures of use.",
  },
  {
    title: "Skill Spec templates + examples",
    description: "Briefs and examples to define the instructions, approved sources, outputs, limits, and review checks for your workflow.",
  },
  {
    title: "AI Leader™ certification",
    description: "Certification that recognizes your team's AI leadership development.",
  },
  {
    title: "30 days of implementation support",
    description: "Weekly implementation office hours and asynchronous help throughout the 30 days. Bring attempted workflows, results, or blockers for coaching and next steps.",
  },
  {
    title: "Lifetime methodology updates",
    description: "Keep access to updates as our method evolves, at no extra cost.",
  },
  {
    title: "Customized to your organization",
    description: "Training shaped around the roles and priorities of company teams, industry groups, and member association cohorts.",
  },
  {
    title: "On-site or virtual delivery",
    description: "Choose the format that works for your leadership team.",
  },
  {
    title: "Guided workflow building",
    description: "Build and test agreed workflows with us using approved tools and data. Your organization owns review, rollout, and ongoing operation.",
  },
  {
    title: "Executive debrief session",
    description: "Review what changed, what the results are worth, and what's getting in the way. Agree on what to continue, revise, or stop.",
  },
];

// Public explainers reviewed for the current 30-day offer.
// Review record: docs/sources/ai30-conversion-proof-2026-10-31.md.
export const AI30_TEACHING_PREVIEW_VIDEOS = [
  {
    id: "ai-fundamentals",
    title: "AI-First Fundamentals",
    youtubeId: "zwPwsA5XAgw",
    description: "An explainer on choosing useful work, defining a result, and comparing tools.",
    thumbnail: "https://img.youtube.com/vi/zwPwsA5XAgw/maxresdefault.jpg",
  },
  {
    id: "ai-training-overview",
    title: "AI Tools and Workflows",
    youtubeId: "J67fraMIIfQ",
    description: "An explainer on workflow steps, context, and human review.",
    thumbnail: "https://img.youtube.com/vi/J67fraMIIfQ/maxresdefault.jpg",
  },
];

export const AI_LEADERS_TRAINING_VIDEOS = AI30_TEACHING_PREVIEW_VIDEOS;
